/**
 * Signing in to a Cognito user pool, with `fetch` and nothing else.
 *
 * Shared by both clients so that there is one implementation of the three
 * calls either of them makes: password sign-in, refresh, and revocation.
 *
 * **`USER_PASSWORD_AUTH`, not SRP.** The password goes to Cognito over TLS
 * rather than being proven without being sent. That is the same exposure as
 * every other login form on the web, and what it buys is no client library:
 * SRP in a browser and in React Native means a third-party package plus
 * crypto polyfills in each, for a property TLS already covers in transit. The
 * pool clients in `infra/stacks/auth_stack.py` allow this flow and no other.
 *
 * Errors carry Cognito's own type name. Both pool clients set
 * `prevent_user_existence_errors`, so a wrong password and an unknown address
 * are both `NotAuthorizedException` -- a caller cannot tell them apart, and
 * should not try.
 */

export const COGNITO_REGION = 'eu-west-2';

const ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

export interface CognitoTokens {
  readonly idToken: string;
  readonly refreshToken: string;
  /**
   * For Cognito's own self-service calls — changing a password, managing
   * passkeys — and nothing else: the API takes the ID token. Never stored by
   * either client; asked for fresh when one of those calls needs it.
   */
  readonly accessToken: string;
}

export class CognitoError extends Error {
  constructor(
    /** e.g. `NotAuthorizedException`, or `NetworkError` when nothing came back. */
    readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = 'CognitoError';
  }
}

/**
 * One Cognito Identity Provider call, unauthenticated or carrying an access
 * token in its body, as Cognito's self-service operations do. Exported for the
 * dashboard's passkey code, which needs the browser's WebAuthn API and so
 * cannot live in this file, which the phone app also compiles.
 */
export async function cognitoRequest(
  target: string,
  body: unknown,
  keepalive = false,
): Promise<Record<string, unknown>> {
  return call(target, body, keepalive);
}

async function call(
  target: string,
  body: unknown,
  keepalive = false,
): Promise<Record<string, unknown>> {
  let response: Response;
  try {
    response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-amz-json-1.1',
        'x-amz-target': `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
      keepalive,
    });
  } catch {
    throw new CognitoError('NetworkError', 'Could not reach the sign-in service.');
  }

  let parsed: Record<string, unknown> = {};
  try {
    parsed = (await response.json()) as Record<string, unknown>;
  } catch {
    // An empty body is a success for RevokeToken, and a failure below.
  }

  if (!response.ok) {
    // `__type` is sometimes prefixed with a namespace and a '#'.
    const type = String(parsed['__type'] ?? 'UnknownError').split('#').pop() ?? 'UnknownError';
    const message = typeof parsed['message'] === 'string' ? parsed['message'] : type;
    throw new CognitoError(type, message);
  }
  return parsed;
}

export function authenticationResult(response: Record<string, unknown>): Record<string, unknown> {
  return result(response);
}

/** Tokens from a completed sign-in, whichever way it was done. */
export function tokensFrom(response: Record<string, unknown>): CognitoTokens {
  const auth = result(response);
  const idToken = auth['IdToken'];
  const refreshToken = auth['RefreshToken'];
  const accessToken = auth['AccessToken'];
  if (
    typeof idToken !== 'string' ||
    typeof refreshToken !== 'string' ||
    typeof accessToken !== 'string'
  ) {
    throw new CognitoError('UnusableResponse', 'The sign-in service returned something unusable.');
  }
  return { idToken, refreshToken, accessToken };
}

function result(response: Record<string, unknown>): Record<string, unknown> {
  const auth = response['AuthenticationResult'];
  if (typeof auth !== 'object' || auth === null) {
    // A challenge -- a new password, MFA. Neither pool is set up to issue one:
    // passwords are set permanent when the user is created, and there is no
    // MFA. Meeting one means the pool has changed under this code.
    const challenge = typeof response['ChallengeName'] === 'string' ? response['ChallengeName'] : '';
    throw new CognitoError(
      'UnexpectedChallenge',
      `Sign-in needs a step this app does not support${challenge ? ` (${challenge})` : ''}.`,
    );
  }
  return auth as Record<string, unknown>;
}

export async function cognitoSignIn(
  clientId: string,
  email: string,
  password: string,
): Promise<CognitoTokens> {
  return tokensFrom(
    await call('InitiateAuth', {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: { USERNAME: email, PASSWORD: password },
    }),
  );
}

/**
 * A fresh ID token for a refresh token.
 *
 * Cognito returns no new refresh token here (rotation is off), so the one
 * passed in is handed back: it stays valid until the expiry the pool client
 * sets, and then the person signs in again.
 */
export async function cognitoRefresh(clientId: string, refreshToken: string): Promise<CognitoTokens> {
  const auth = result(
    await call('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: clientId,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    }),
  );
  const idToken = auth['IdToken'];
  const accessToken = auth['AccessToken'];
  if (typeof idToken !== 'string' || typeof accessToken !== 'string') {
    throw new CognitoError('UnusableResponse', 'The sign-in service returned something unusable.');
  }
  const rotated = auth['RefreshToken'];
  return {
    idToken,
    accessToken,
    refreshToken: typeof rotated === 'string' ? rotated : refreshToken,
  };
}

/**
 * Revoke a refresh token, so that signing out ends the session server-side
 * and not only on this device. Tokens already issued stay valid until they
 * expire -- thirty minutes for a clinician.
 *
 * `keepalive`, because the caller is usually about to reload or close the page,
 * and an ordinary request would be cancelled by that.
 */
export async function cognitoRevoke(clientId: string, refreshToken: string): Promise<void> {
  await call('RevokeToken', { ClientId: clientId, Token: refreshToken }, true);
}

/** Change the signed-in user's password. Needs their current one. */
export async function cognitoChangePassword(
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await call('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}
